const PersonTypeService = require('@transfeeradev/api-services/PersonTypeService');
const { accountType, banks, errors, caixaOperationType } = require('@transfeeradev/api-enums');
const ValidationError = require('./errors/ValidationError');
const Default = require('./Default');

module.exports = class Caixa extends Default {
  constructor(data) {
    super('Caixa', data, banks.CAIXA_ECONOMICA.code);

    this._defaultAccountSchemaLength = 8;

    this._data.agency = this._removeUnnecessaryLeadingZeros(this._data.agency, this._agencySchemaLength);
    this._data.account = this._removeUnnecessaryLeadingZeros(this._data.account, this._defaultAccountSchemaLength);
    if (this._data.account) {
      this._data.accountSuggestion = this._removeUnnecessaryLeadingZeros(
        this._data.account.replace(/^0+/, '').replace(/^(13|23|1|3|6)/g, ''),
        this._defaultAccountSchemaLength
      );
    }
  }

  _validateAgency() {
    super._validateAgency();
    this._data.agency = this._fillZero(this._data.agency, this._agencySchemaLength);
  }

  _validateAccount() {
    super._validateAccount();

    if (this._isNewBetaAccount()) {
      return;
    }

    if (this._data.account_type === accountType.ENTIDADES_PUBLICAS && !PersonTypeService.isCNPJ(this._data.cpf_cnpj)) {
      const error = errors.ptBr.dba.ENTIDADE_PUBLICA_IS_JUST_FOR_CNPJ_2;
      throw new ValidationError(error.message, error.field, null, error.code);
    }

    const accountDigits = this._getAccountDigits();
    let accDigits = accountDigits[this._data.account_type];

    if (!accDigits.includes(this._data.account_digit)) {
      if (this.getAccountDigit(this._data.accountSuggestion).toString() === this._data.account_digit) {
        const error = errors.ptBr.dba.INVALID_ACCOUNT;
        throw new ValidationError(
          error.message,
          error.field,
          {
            account: this._data.accountSuggestion.replace(/^0+/, '')
          },
          error.code
        );
      }
      Object.keys(accountDigits).forEach(account_type => {
        if (accountDigits[account_type].includes(this._data.account_digit)) {
          const error = errors.ptBr.dba.ACCOUNT_TYPE_2;
          throw new ValidationError(
            error.message,
            error.field,
            {
              account_type
            },
            error.code
          );
        }
      });

      let suggestion = {
        account_digit: accDigits[0]
      };

      if (this._data.account !== this._data.accountSuggestion && Number.isNaN(Number(accDigits[0]))) {
        suggestion = undefined;
      }

      const error = errors.ptBr.dba.AGENCY_ACCOUNT_TYPE_ACCOUNT_OR_ACCOUNT_DIGIT_INVALID;
      throw new ValidationError(error.message, error.field, suggestion, error.code);
    }
  }

  _getAccountDigits() {
    const getSecondaryOperation = true;
    return {
      CONTA_CORRENTE: [this.getAccountDigit(this._data.account, accountType.CONTA_CORRENTE).toString()],
      CONTA_POUPANCA: [
        this.getAccountDigit(this._data.account, accountType.CONTA_POUPANCA).toString(),
        this.getAccountDigit(this._data.account, accountType.CONTA_POUPANCA, getSecondaryOperation).toString()
      ],
      CONTA_FACIL: [this.getAccountDigit(this._data.account, accountType.CONTA_FACIL).toString()],
      ENTIDADES_PUBLICAS: [this.getAccountDigit(this._data.account, accountType.ENTIDADES_PUBLICAS).toString()]
    };
  }

  _isNewBetaAccount() {
    return (
      this._data.account.length === this._accountSchemaLength && this._data.accountSuggestion === this._data.account
    );
  }

  _getOperationType(dataAccountType) {
    if (dataAccountType === accountType.CONTA_FACIL) {
      return [caixaOperationType.CONTA_FACIL];
    } else if (dataAccountType === accountType.ENTIDADES_PUBLICAS) {
      return [caixaOperationType.ENTIDADES_PUBLICAS];
    } else if (PersonTypeService.isCPF(this._data.cpf_cnpj)) {
      if (dataAccountType === accountType.CONTA_CORRENTE) {
        return [caixaOperationType.CONTA_CORRENTE_PESSOA_FISICA];
      }

      return [caixaOperationType.POUPANCA];
    } else {
      if (dataAccountType === accountType.CONTA_CORRENTE) {
        return [caixaOperationType.CONTA_CORRENTE_PESSOA_JURIDICA];
      }

      return [caixaOperationType.POUPANCA_PESSOA_JURIDICA, caixaOperationType.POUPANCA];
    }
  }

  getAccountDigit(account = this._data.account, accountType = this._data.account_type, getSecondaryOperation = false) {
    const multipliers1 = [8, 7, 6, 5, 4, 3, 2];
    const multipliers2 = [9, 8, 7, 6, 5, 4, 3, 2];
    const operations = this._getOperationType(accountType);
    const operation = getSecondaryOperation && operations[1] ? operations[1] : operations[0];
    const bankNumber = this._fillZero(this._data.agency, this._agencySchemaLength) + this._fillZero(operation, 3);
    const bankAccount = this._fillZero(account, this._defaultAccountSchemaLength);
    let result = 0;

    for (let i = 0; i < bankNumber.length; i++) {
      result += Number(bankNumber[i]) * multipliers1[i];
    }

    for (let i = 0; i < bankAccount.length; i++) {
      result += Number(bankAccount[i]) * multipliers2[i];
    }

    result = result * 10;

    result = result % 11;
    if (result === 10) {
      return '0';
    }

    return String(result);
  }

  getOperation() {
    const operation = this._getOperationType(this._data.account_type);
    if (operation.length === 1) {
      return operation[0];
    }

    const digit = this.getAccountDigit(this._data.account, this._data.account_type);
    const getSecondaryOperation = true;
    const secondaryDigit = this.getAccountDigit(this._data.account, this._data.account_type, getSecondaryOperation);
    if (digit === this._data.account_digit) {
      return operation[0];
    } else if (secondaryDigit === this._data.account_digit) {
      return operation[1];
    }

    return null;
  }
};
